import React, { useEffect, useState, useRef, useCallback } from 'react';
import { proxy, useSnapshot } from 'valtio';
import axios from 'redaxios';
import { getAxiosWithAuth } from '../../api/repository';
import { updateLocalStorage } from '../../helper/utils';
import { info } from '@knowins/notifier';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import RestoreIcon from '@mui/icons-material/Restore';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ArchiveIcon from '@mui/icons-material/Archive';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ChatIcon from '@mui/icons-material/Person3Sharp';

import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Collapse from '@mui/material/Collapse';

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import Grid from '@mui/material/Unstable_Grid2';
import InputAdornment from '@mui/material/InputAdornment';
import AccountCircle from '@mui/icons-material/AccountCircle';
import SendIcon from '@mui/icons-material/SendSharp';

import { Typography, Link, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';

import Paper, { PaperProps } from '@mui/material/Paper';
import { Rnd } from 'react-rnd';

import store, { getChat } from './store/ChatStore';
import { invite, text } from './hook/useInitialization';
import { executePromise } from '@knowins/notifier';

const room = {
  agent: 'agent',
  personal: 'private',
  internal: 'internal',
} as const;

const defaultSize = {
  width: 600,
  height: window.innerHeight - 74,
};

const defaultPositionFn = () => {
  return {
    x: window.innerWidth - defaultSize.width - 13,
    y: window.innerHeight - defaultSize.height - 68,
  };
};

export default function DraggableDialog() {
  const { t } = useTranslation();
  const messageEl = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const state = useSnapshot(store);
  const [publicMessages, setPublicMessages] = useState<unknown[]>([]);
  const [bookkeepingSessionId, setBookkeepingSessionId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [wsConnection, setWsConnection] = useState<WebSocket | null>(null);
  const [wsConnected, setWsConnected] = useState(false);
  const wsConnectionRef = useRef<WebSocket | null>(null);

  // WebSocket connection management
  const connectWebSocket = useCallback(() => {
    // Check if already connected
    if (wsConnectionRef.current && wsConnectionRef.current.readyState === WebSocket.OPEN) {
      console.log('[WEBSOCKET] Already connected, skipping reconnect');
      return; // Already connected
    }

    const token = localStorage.getItem('jwt_token');
    // Connect directly to bookkeeping agent in development, use proxy in production
    const baseWsUrl =
      window.location.host === 'localhost:3000'
        ? `ws://localhost:3008/ws/chat` // Direct connection to bookkeeping agent
        : window.location.host.includes('localhost')
        ? `ws://${window.location.host.replace('3000', '3008')}/ws/chat`
        : `wss://${window.location.host}/ws/chat`;
    const wsUrl = token ? `${baseWsUrl}?token=${encodeURIComponent(token)}` : baseWsUrl;

    console.log('[WEBSOCKET] Connecting to', wsUrl);
    const ws = new WebSocket(wsUrl);
    wsConnectionRef.current = ws;

    ws.onopen = () => {
      console.log('[WEBSOCKET] Connected');
      setWsConnected(true);
      setWsConnection(ws);

      // Always request conversation history/session initialization
      // If we have a session ID, get existing history; if not, create new session with initial greeting
      const historyRequest = {
        action: 'get_history',
        session_id: bookkeepingSessionId || null, // Send null for new sessions
      };
      console.log(
        '[WEBSOCKET] Requesting session initialization/history:',
        bookkeepingSessionId || 'new session',
      );
      ws.send(JSON.stringify(historyRequest));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[WEBSOCKET] Received:', data);

        // Store server-returned session ID for subsequent messages
        if (data.session_id && data.session_id !== bookkeepingSessionId) {
          console.log('[WEBSOCKET] Updating session ID from server:', data.session_id);
          setBookkeepingSessionId(data.session_id);
          // Persist session ID to localStorage for continuity across component remounts
          localStorage.setItem('bookkeeping-session-id', data.session_id);
        }

        // Handle conversation history for resumed sessions
        if (data.conversation_history && Array.isArray(data.conversation_history)) {
          console.log(
            '[WEBSOCKET] Received conversation history:',
            data.conversation_history.length,
            'messages',
          );
          // Convert server ClaudeMessage format to frontend format
          const historyMessages = data.conversation_history.map((msg: unknown) => ({
            timestamp: Date.now(), // Use current timestamp since server messages don't have timestamps
            message: (msg as { content?: string }).content || '',
            author: (msg as { role?: string }).role === 'user' ? 'You' : 'Mr. K.A.',
            isAgent: (msg as { role?: string }).role !== 'user',
          }));
          setPublicMessages(historyMessages);
          return; // Don't process other message types for history messages
        }

        if (data.success && data.response) {
          const agentMsg = {
            timestamp: Date.now(),
            message: data.response,
            author: 'Mr. K.A.',
            isAgent: true,
          };
          setPublicMessages((prev) => [...prev, agentMsg]);
        } else if (
          data.is_session_expired ||
          (data.error && data.error.includes('Session expired'))
        ) {
          // Handle session expired - log user out
          console.log('[WEBSOCKET] Session expired detected, logging out user');
          info('status.session_expired', {
            fn: () => {
              updateLocalStorage({ login: false });
              localStorage.removeItem('jwt_token');
              window.location.reload();
            },
          });

          const expiredMsg = {
            timestamp: Date.now(),
            message: data.error || 'Session expired. Please refresh your session and try again.',
            author: 'System',
            isAgent: false,
          };
          setPublicMessages((prev) => [...prev, expiredMsg]);
        } else if (data.error) {
          const errorMsg = {
            timestamp: Date.now(),
            message: data.error,
            author: 'System',
            isAgent: false,
          };
          setPublicMessages((prev) => [...prev, errorMsg]);
        } else if (data.progress) {
          const progressMsg = {
            timestamp: Date.now(),
            message: data.progress,
            author: 'Mr. K.A.',
            isAgent: true,
            isProgress: true,
          };
          setPublicMessages((prev) => [...prev, progressMsg]);
        }
      } catch (e) {
        console.error('[WEBSOCKET] Failed to parse message:', e);
      }
    };

    ws.onclose = () => {
      console.log('[WEBSOCKET] Disconnected');
      setWsConnected(false);
      setWsConnection(null);
      wsConnectionRef.current = null;
    };

    ws.onerror = (error) => {
      console.error('[WEBSOCKET] Error:', error);
      setWsConnected(false);
    };
  }, [bookkeepingSessionId]);

  const disconnectWebSocket = useCallback(() => {
    if (wsConnectionRef.current) {
      wsConnectionRef.current.close();
      wsConnectionRef.current = null;
      setWsConnection(null);
      setWsConnected(false);
    }
  }, []);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    if (messageEl?.current) {
      messageEl.current.scrollTop = messageEl.current.scrollHeight;
    }
  }, [publicMessages]);

  useEffect(() => {
    // Load saved session ID from localStorage on mount
    const savedSessionId = localStorage.getItem('bookkeeping-session-id');
    if (savedSessionId) {
      setBookkeepingSessionId(savedSessionId);
    }

    // Cleanup on unmount
    return () => {
      // Disconnect WebSocket when component unmounts
      if (wsConnectionRef.current) {
        wsConnectionRef.current.close();
      }
    };
  }, []); // Empty dependency array - run only once on mount

  // Auto-initialize agent conversation when agent room is selected
  useEffect(() => {
    console.log('[DEBUG] Auto-init effect triggered:', {
      session: state.session,
      room: room.agent,
      wsConnected: wsConnected,
    });

    if (state.session === room.agent) {
      // Ensure WebSocket is connected when in agent room
      if (
        !wsConnected &&
        (!wsConnectionRef.current || wsConnectionRef.current.readyState !== WebSocket.OPEN)
      ) {
        connectWebSocket();
      }
    } else {
      // Disconnect WebSocket when leaving agent room
      if (wsConnected) {
        disconnectWebSocket();
      }
    }
  }, [state.session, wsConnected, connectWebSocket, disconnectWebSocket]);

  const myName = localStorage.getItem('operatorname') || localStorage.getItem('moneta') || '';
  const wallet = localStorage.getItem('wallet');
  const authorAccount = `eip155:1:${wallet}`;

  const chatters = state.session === room.personal ? state.private : [];
  const { handleSubmit, setValue, control } = useForm({
    defaultValues: {
      address: store.current,
      message: '',
    },
  });

  // Sync form address field with store.current whenever it changes
  useEffect(() => {
    if (state.session === room.personal && store.current) {
      setValue('address', store.current);
    }
  }, [state.session, setValue]);

  const isOutgoingMessage = (account: string) => account === authorAccount;

  const goToAgentRoom = () => {
    store.session = room.agent;
  };

  const goToPrivatecRoom = () => {
    store.session = room.personal;
  };

  const changeChatter = (address) => {
    store.current = address;
    setValue('address', address);
  };

  const sendMessageToAgent = async (message: string) => {
    try {
      setIsLoading(true);

      // Ensure WebSocket is connected
      if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN) {
        console.log('[WEBSOCKET] Not connected, attempting to connect...');
        connectWebSocket();

        // Wait a bit for connection
        await new Promise((resolve) => setTimeout(resolve, 1000));

        if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN) {
          throw new Error('WebSocket connection failed');
        }
      }

      // Add user message to agent chat
      const userMsg = {
        timestamp: Date.now(),
        message,
        author: 'You',
        isAgent: false,
      };
      setPublicMessages((prev) => [...prev, userMsg]);

      // Send message through WebSocket
      const payload = {
        message,
        session_id: bookkeepingSessionId,
      };

      console.log('[WEBSOCKET] Sending:', payload);
      wsConnection.send(JSON.stringify(payload));
    } catch (error: unknown) {
      console.error('[WEBSOCKET] Send error:', error);
      const errorMsg = {
        timestamp: Date.now(),
        message: `Error: ${(error as { message?: string }).message || 'Unknown error'}`,
        author: 'System',
        isAgent: false,
      };
      setPublicMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: { address: string; message: string }) => {
    const { address, message } = data;
    if (message.length === 0) {
      return;
    }

    if (state.session === room.agent) {
      // Agent room - chat with Bookkeeping Agent
      await sendMessageToAgent(message);
    } else if (state.session === room.personal) {
      const chat = getChat(address);
      if (chat && store.private[chat].accepted) {
        //const chatmates = setValue('message', '');
        const topic = store.private[chat].topic;
        console.log('in chat with.....', chat, topic);

        store.private[chat].payloads = [
          ...store.private[chat].payloads,
          {
            timestamp: Date.now(),
            message,
            authorAccount,
          },
        ];

        text(topic, message);
      } else {
        executePromise(
          getAxiosWithAuth().post('/api/router.php', {
            action: 'FrmUser',
            method: 'getUserWallet',
            data: [{ address }],
            type: 'rpc',
          }),
          {
            loading: 'status.sending_request',
            success: (res) => {
              const { moneta: chat, wallet, fullname, email } = res.data;
              invite(wallet, myName, message).then((id) => {
                store.private[chat] = {
                  // invitation_id: id,
                  email,
                  fullname: fullname || chat,
                  // topic: '', topic is NOT available yet
                  adddress: `eip155:1:${wallet}`,
                  payloads: [{ timestamp: Date.now(), message, authorAccount }],
                };
                store.current = chat;
              });
              return 'status.sent_chat_invitation';
            },
          },
        );
      }
    }
    setValue('message', '');
  };
  const activeChat = state.session === room.personal ? state.private[state.current] : undefined;
  const isPublicChat = state.session === room.agent;

  return (
    <Rnd
      default={{
        ...defaultPositionFn(),
        width: defaultSize.width,
        height: defaultSize.height,
      }}
      dragHandleClassName="drag-handle"
      style={{ zIndex: 10000 }}
      enableResizing={false}
      minWidth={defaultSize.width}
      minHeight={defaultSize.height}
      maxWidth={defaultSize.width}
      maxHeight={defaultSize.height}
      disableDragging={false}
      cancel="input, textarea, button, .MuiInputBase-root, .MuiTextField-root, .MuiButton-root, [role='button']"
    >
      <form
        style={{
          width: '100%',
          height: '100%',
          margin: 0,
          padding: 0,
        }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Card
          style={{
            width: '100%',
            height: '100%',
            margin: 0,
            padding: 0,
          }}
        >
          <CardHeader
            style={{
              cursor: 'move',
              padding: '0 16px',
              height: 60,
              bgcolor: 'background.paper',
            }}
            className="drag-handle"
            subheader={
              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    sx={{ width: 268 }}
                    margin="dense"
                    placeholder={t('chat.receiver')}
                    variant="standard"
                    value={state.session === room.agent ? 'Mr. KNOW-ALL' : field.value}
                    disabled={state.session === room.agent}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ChatIcon />
                        </InputAdornment>
                      ),
                      readOnly: state.session === room.agent,
                    }}
                  />
                )}
              />
            }
          ></CardHeader>
          <CardContent sx={{ p: '0 16px', height: 'calc(100% - 120px)' }}>
            <div style={{ display: 'flex', flexFlow: 'column', height: '100%' }}>
              {state.session === room.personal && (
                <Grid sx={{ m: 0, p: 0, height: 68 }} container spacing={1}>
                  {Object.keys(chatters).map((k) => {
                    return (
                      <Grid key={k} xs display="flex" justifyContent="center" alignItems="center">
                        <IconButton
                          sx={{ display: 'block' }}
                          color="secondary"
                          size="small"
                          onClick={() => changeChatter(k)}
                        >
                          <Avatar
                            alt={k.toUpperCase()}
                            src={'/api/get/avatar.php?moneta=' + k}
                            style={{
                              backgroundColor: state.current === k ? '#cc008c' : '#edf2ff',
                            }}
                          />
                          <Typography color="primary" sx={{ lineHeight: 0.6 }}>
                            {k}
                          </Typography>
                        </IconButton>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
              <Paper
                variant="outlined"
                style={{ flex: 1, overflow: 'auto' }}
                ref={messageEl as any} // eslint-disable-line @typescript-eslint/no-explicit-any
                sx={{
                  borderLeft: '1px solid red',
                }}
              >
                {isPublicChat ? (
                  <List
                    sx={{
                      height: '100%',
                      bgcolor: 'background.paper',
                    }}
                  >
                    {publicMessages.map(
                      (
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        p: any,
                        idx,
                      ) => (
                        <React.Fragment key={`${p.timestamp}-${idx}`}>
                          <ListItem
                            alignItems="flex-start"
                            sx={{
                              paddingTop: 0,
                              paddingBottom: 0,
                              borderBottom:
                                publicMessages[idx + 1] === undefined ? 0 : '0.5px solid #e6eaef',
                            }}
                          >
                            <ListItemText
                              primary={undefined}
                              primaryTypographyProps={{
                                sx: {
                                  fontSize: 13,
                                  lineHeight: 0.8,
                                  fontWeight: 700,
                                  marginBottom: 2,
                                  color: p.isAgent ? '#1976d2' : 'inherit',
                                },
                              }}
                              secondary={
                                p.isAgent && p.message.startsWith('Mr. K.A.:') ? (
                                  <span>
                                    <strong>Mr. K.A.:</strong>
                                    {p.message.substring('Mr. K.A.:'.length)}
                                  </span>
                                ) : (
                                  p.message
                                )
                              }
                              secondaryTypographyProps={{
                                component: 'span',
                                sx: {
                                  fontSize: 13,
                                  lineHeight: 1.4,
                                  paddingTop: 0,
                                  paddingBottom: 0,
                                  textAlign: p.isAgent ? 'start' : 'end',
                                  whiteSpace: 'pre-wrap',
                                  color: p.isProgress
                                    ? '#ff9800'
                                    : p.isAgent
                                    ? '#424242'
                                    : '#1976d2', // Orange for progress, dark gray for agent, blue for user
                                  fontStyle: p.isProgress ? 'italic' : 'normal',
                                },
                              }}
                            />
                          </ListItem>
                        </React.Fragment>
                      ),
                    )}
                    {isLoading && (
                      <ListItem>
                        <ListItemText
                          secondary="Agent is thinking..."
                          secondaryTypographyProps={{
                            component: 'span',
                            sx: {
                              fontSize: 13,
                              fontStyle: 'italic',
                              color: '#666',
                            },
                          }}
                        />
                      </ListItem>
                    )}
                    {!wsConnected && state.session === room.agent && (
                      <ListItem>
                        <ListItemText
                          secondary="🔴 Connecting... knock, knock Mr. K.A."
                          secondaryTypographyProps={{
                            component: 'span',
                            sx: {
                              fontSize: 12,
                              fontStyle: 'italic',
                              color: '#ff6b6b',
                            },
                          }}
                        />
                      </ListItem>
                    )}
                    {wsConnected && state.session === room.agent && !isLoading && (
                      <ListItem>
                        <ListItemText
                          secondary="🟢 Ready"
                          secondaryTypographyProps={{
                            component: 'span',
                            sx: {
                              fontSize: 12,
                              fontStyle: 'italic',
                              color: '#51cf66',
                            },
                          }}
                        />
                      </ListItem>
                    )}
                    {wsConnected && state.session === room.agent && isLoading && (
                      <ListItem>
                        <ListItemText
                          secondary="💭 Thinking..."
                          secondaryTypographyProps={{
                            component: 'span',
                            sx: {
                              fontSize: 12,
                              fontStyle: 'italic',
                              color: '#339af0',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              '@keyframes pulse': {
                                '0%, 100%': {
                                  opacity: 1,
                                },
                                '50%': {
                                  opacity: 0.5,
                                },
                              },
                            },
                          }}
                        />
                      </ListItem>
                    )}
                  </List>
                ) : activeChat ? (
                  <List
                    sx={{
                      height: '100%',
                      bgcolor: 'background.paper',
                    }}
                  >
                    {activeChat.payloads.map((p, idx) => (
                      <React.Fragment key={`${p.timestamp}-${idx}`}>
                        <ListItem
                          alignItems="flex-start"
                          sx={{
                            paddingTop: 0,
                            paddingBottom: 0,
                            borderBottom:
                              activeChat.payloads[idx + 1] === undefined
                                ? 0
                                : activeChat.payloads[idx + 1]?.authorAccount === p.authorAccount
                                ? 0
                                : '0.5px solid #e6eaef',
                          }}
                        >
                          <ListItemText
                            primary={
                              idx > 0 &&
                              p.authorAccount === activeChat.payloads[idx - 1]?.authorAccount
                                ? undefined
                                : isOutgoingMessage(p.authorAccount)
                                ? myName
                                : activeChat.fullname
                            }
                            primaryTypographyProps={{
                              sx: {
                                fontSize: 13,
                                lineHeight: 0.8,
                                fontWeight: 700,
                                marginBottom: 2,
                              },
                            }}
                            secondary={p.message}
                            secondaryTypographyProps={{
                              component: 'span',
                              sx: {
                                fontSize: 13,
                                lineHeight: 0.8,
                                paddingTop: 0,
                                paddingBottom: 0,
                                textAlign: isOutgoingMessage(p.authorAccount) ? 'end' : 'start',
                              },
                            }}
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                ) : null}
              </Paper>
              <div
                style={{
                  height: 53,
                  userSelect: 'text',
                  WebkitUserSelect: 'text',
                  MozUserSelect: 'text',
                  msUserSelect: 'text',
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <Controller
                  name="message"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      inputRef={inputRef}
                      size="small"
                      margin="dense"
                      fullWidth
                      disabled={isLoading}
                      label={isPublicChat ? 'Ask Mr. K.A.' : t('chat.message')}
                      placeholder={isPublicChat ? 'e.g., guest 2024001 to 2024012' : ''}
                      variant="outlined"
                      autoFocus={isPublicChat}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Ensure input gets focus
                        setTimeout(() => {
                          if (inputRef.current) {
                            inputRef.current.focus();
                          }
                        }, 0);
                      }} // Allow input focus
                      onFocus={(e) => e.stopPropagation()} // Prevent drag on focus
                      onDragStart={(e) => e.preventDefault()} // Prevent any drag start
                      inputProps={{
                        onMouseDown: (e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }, // Prevent drag when clicking input
                        onClick: (e) => {
                          e.stopPropagation();
                          // Ensure input maintains focus
                          if (inputRef.current) {
                            inputRef.current.focus();
                          }
                        },
                        onDragStart: (e) => e.preventDefault(), // Prevent drag start on input
                      }}
                      InputProps={{
                        onMouseDown: (e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }, // Prevent drag when clicking input container
                        onDragStart: (e) => e.preventDefault(), // Prevent drag start on container
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              type="submit"
                              disabled={isLoading}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }} // Prevent drag when clicking button
                              onDragStart={(e) => e.preventDefault()} // Prevent drag start on button
                            >
                              <SendIcon color={isLoading ? 'disabled' : 'primary'} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </div>
            </div>
          </CardContent>
          <CardActions sx={{ height: 60, p: 1 }}>
            <Paper sx={{ width: '100%' }} elevation={3}>
              <BottomNavigation
                showLabels
                value={state.session === room.agent ? 0 : state.session === room.personal ? 1 : 2}
                onChange={(event, newValue) => {
                  if (newValue === 0) goToAgentRoom();
                  else if (newValue === 1) goToPrivatecRoom();
                  // Team (index 2) is disabled
                }}
              >
                <BottomNavigationAction
                  label="Mr. KNOW-ALL"
                  icon={
                    <RestoreIcon color={state.session === room.agent ? 'primary' : 'secondary'} />
                  }
                />
                <BottomNavigationAction
                  label="Private"
                  icon={
                    <FavoriteIcon
                      color={state.session === room.personal ? 'primary' : 'secondary'}
                    />
                  }
                />
                <BottomNavigationAction
                  disabled
                  label="Team"
                  icon={
                    <ArchiveIcon
                      color={state.session === room.internal ? 'primary' : 'secondary'}
                    />
                  }
                />
              </BottomNavigation>
            </Paper>
          </CardActions>
        </Card>
      </form>
    </Rnd>
  );
}
